import React, {
  useState,
  useMemo,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useCallback,
} from "react";
import axios from "axios";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import ProcessingPopup from "../../../../utils/popup/ProcessingPopup";
import ErrorPopup from "../../../../utils/popup/ErrorPopup";
import ConfirmationModal from "../../../../utils/ConfirmationModal";
import GetCookie from "../../../../utils/GetCookie";
import authNavigate from "../../../../globalComponents/AuthNav";
import { useNavigate } from "react-router-dom";
import { PICO_COLORS, normalizePicoKey } from "../../../../utils/Picocolors";

const makeId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;
const API_BASE_URL = "/pceapi/slr/form";

const PICOS_ENUM_MAP = {
  Population: "POPULATION",
  Intervention: "INTERVENTION",
  Comparator: "COMPARISON",
  Outcome: "OUTCOME",
  "Study Design": "STUDY_DESIGN",
  Exclusion: "EXCLUSION",
};

const TAG_SOURCE_ENUM_MAP = {
  "Manually added": "MANUAL",
  "AI-generated": "AI",
};

const REVERSE_PICOS_ENUM_MAP = {
  POPULATION: "Population",
  INTERVENTION: "Intervention",
  COMPARISON: "Comparator",
  OUTCOME: "Outcome",
  STUDY_DESIGN: "Study Design",
  EXCLUSION: "Exclusion",
  OTHER: "Other",
};

const REVERSE_TAG_SOURCE_ENUM_MAP = {
  MANUAL: "Manually added",
  AI: "AI-generated",
};

const VALID_PICOS_ENUMS = new Set([
  "POPULATION",
  "INTERVENTION",
  "COMPARISON",
  "OUTCOME",
  "STUDY_DESIGN",
  "EXCLUSION",
  "OTHER",
]);

const PICOS_ORDER = [
  "POPULATION",
  "INTERVENTION",
  "COMPARISON",
  "OUTCOME",
  "STUDY_DESIGN",
  "EXCLUSION",
];

const normalizePicosCategory = (picosValue) => {
  const v = String(picosValue || "").trim();
  if (VALID_PICOS_ENUMS.has(v)) return v; // already enum
  return PICOS_ENUM_MAP[v] || "OTHER"; // label -> enum, else OTHER
};

const normalizeFrequency = (value) => {
  if (value === "" || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const ScreeningTaggingForm = forwardRef(
  ({ projectId, stageData, onValidityChange, onDataChange }, ref) => {
    const navigate = useNavigate();
    const headers = useMemo(() => ({ Authorization: GetCookie("access_token") }), []);

    const [rows, setRows] = useState([]);
    const [aiPrompt, setAiPrompt] = useState("");
    const [useProtocol, setUseProtocol] = useState(false);
    const [sampleSize, setSampleSize] = useState(400);
    const [isProcessingPopupVisible, setIsProcessingPopupVisible] =
      useState(false);
    const [processingMessage, setProcessingMessage] = useState("");

    const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
    const [popupErrorMessage, setPopupErrorMessage] = useState("");

    // Collapse state
    const [expandedCategories, setExpandedCategories] = useState(new Set());
    const [expandedSubcategories, setExpandedSubcategories] = useState(new Set());
    const [showGenerateConfirm, setShowGenerateConfirm] = useState(false);

    // Build payload for save
    const buildTagsPayload = useCallback((rowsList) => {
      const list = Array.isArray(rowsList) ? rowsList : [];
      return list
        .filter((r) => (r.tag || "").trim().length > 0)
        .map((r) => ({
          picos_category: normalizePicosCategory(r.picos),
          subcategory: r.subcategory || "Unclassified",
          tag: (r.tag || "").trim(),
          source: TAG_SOURCE_ENUM_MAP[r.source] || "MANUAL",
          sample_frequency: normalizeFrequency(r.frequency),
        }));
    }, []);

    useImperativeHandle(ref, () => ({
      getTagsData: () => buildTagsPayload(rows),
    }));

    // Group rows into hierarchy for rendering: { CATEGORY: { subcategory: [rows] } }
    const grouped = useMemo(() => {
      const map = {};
      PICOS_ORDER.forEach((cat) => { map[cat] = { Unclassified: [] }; });

      rows.forEach((row) => {
        const cat = normalizePicosCategory(row.picos);
        const sub = row.subcategory || "Unclassified";
        if (!map[cat]) map[cat] = { Unclassified: [] };
        if (!map[cat][sub]) map[cat][sub] = [];
        map[cat][sub].push(row);
      });

      // Keep all defined categories (so user can always add manually)
      return map;
    }, [rows]);

    // Sorted categories (maintain PICOS_ORDER, then any extras)
    const sortedCategories = useMemo(() => {
      const keys = Object.keys(grouped);
      const ordered = PICOS_ORDER.filter((k) => keys.includes(k));
      const extra = keys.filter((k) => !PICOS_ORDER.includes(k));
      return [...ordered, ...extra];
    }, [grouped]);

    const addTagInSubcategory = (picosEnum, subcategory) => {
      setRows((prev) => [
        ...prev,
        {
          id: makeId(),
          picos: REVERSE_PICOS_ENUM_MAP[picosEnum] || picosEnum,
          subcategory: subcategory,
          tag: "",
          source: "Manually added",
          frequency: "",
        },
      ]);
    };

    const updateRow = (id, patch) => {
      setRows((prev) =>
        prev.map((r) => (r.id === id ? { ...r, ...patch } : r)),
      );
    };

    const removeRow = (id) => {
      setRows((prev) => prev.filter((r) => r.id !== id));
    };

    const totalTagsCount = rows.filter(
      (r) => (r.tag || "").trim().length > 0,
    ).length;

    const isStepValid =
      rows.length > 0 && rows.every((r) => (r.tag || "").trim().length > 0);

    useEffect(() => {
      onValidityChange?.(isStepValid);
    }, [isStepValid, onValidityChange]);

    useEffect(() => {
      onDataChange?.(buildTagsPayload(rows));
    }, [rows, onDataChange, buildTagsPayload]);

    const allSubs = useMemo(() => {
        const subs = new Set();
        sortedCategories.forEach(cat => {
            Object.keys(grouped[cat] || {}).forEach(sub => {
                subs.add(`${cat}::${sub}`);
            });
        });
        return subs;
    }, [sortedCategories, grouped]);

    const areAllExpanded = expandedCategories.size >= sortedCategories.length && expandedSubcategories.size >= allSubs.size && sortedCategories.length > 0;
    
    const toggleExpandAll = () => {
      if (areAllExpanded) {
        // Collapse all (clear expanded sets)
        setExpandedCategories(new Set());
        setExpandedSubcategories(new Set());
      } else {
        // Expand all (populate with all keys)
        setExpandedCategories(new Set(sortedCategories));
        setExpandedSubcategories(allSubs);
      }
    };

    // Toggle expand
    const toggleCategory = (cat) => {
      setExpandedCategories((prev) => {
        const next = new Set(prev);
        if (next.has(cat)) next.delete(cat);
        else next.add(cat);
        return next;
      });
    };

    const toggleSubcategory = (key) => {
      setExpandedSubcategories((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    };

    const onDragEnd = (result) => {
      const { draggableId, source, destination } = result;
      if (!destination) return;

      const [destCat, destSub] = destination.droppableId.split("::");
      const [srcCat, srcSub] = source.droppableId.split("::");
      if (!destCat || !destSub || !srcCat || !srcSub) return;

      setRows((prev) => {
        const inBucket = (r, cat, sub) =>
          normalizePicosCategory(r.picos) === cat &&
          (r.subcategory || "Unclassified") === sub;

        const destBucketRows = prev.filter((r) => inBucket(r, destCat, destSub));

        const draggedRow = prev.find((r) => r.id === draggableId);
        if (!draggedRow) return prev;

        const movedRow =
          srcCat === destCat && srcSub === destSub
            ? draggedRow
            : {
                ...draggedRow,
                picos: REVERSE_PICOS_ENUM_MAP[destCat] || destCat,
                subcategory: destSub,
              };

        const reorderedBucket = destBucketRows.filter((r) => r.id !== draggableId);
        reorderedBucket.splice(destination.index, 0, movedRow);

        const out = [];
        let bucketEmitted = false;
        for (const r of prev) {
          if (r.id === draggableId && !inBucket(r, destCat, destSub)) continue; // drop old slot
          if (inBucket(r, destCat, destSub)) {
            if (!bucketEmitted) {
              out.push(...reorderedBucket);
              bucketEmitted = true;
            }
            continue;
          }
          out.push(r);
        }
        if (!bucketEmitted) out.push(...reorderedBucket);

        return out;
      });
    };

    // AI Generate
    const MODE_DIRECTIVE = {
      add: "Add to the existing tags. Keep every existing tag exactly as it is, with its category, subcategory, source and sample_frequency unchanged, and append new tags only. Instruction:",
      revise: "Revise the existing tags. You may move a tag into a different subcategory AND add new tags in the same answer. Return existing tags with their text UNCHANGED, character for character — the tag text is their only identity, so re-wording one loses it. Keep source and sample_frequency unchanged on existing tags. Do not drop tags: removing a tag is the reviewer's decision, not yours. Instruction:",
      replace: "Start over and generate fresh tags, discarding what exists. Instruction:",
    };

    const doGenerate = async (mode = "replace") => {
      const size = Number(sampleSize);
      if (!Number.isFinite(size) || size <= 0) {
        setPopupErrorMessage("Please enter a valid sample size greater than 0");
        setIsErrorPopupVisible(true);
        return;
      }

      setIsProcessingPopupVisible(true);
      setProcessingMessage("Generating tags using AI");

      try {
        const response = await axios.post(
          `${API_BASE_URL}/generate-tags`,
          {
            fabric_id: projectId,
            prompt: `${MODE_DIRECTIVE[mode]} ${aiPrompt}`,
            sample_size: size,
            use_protocol: useProtocol,
          },
          { headers },
        );

        const data = response.data;

        if (data.status === "NotAuthorized") {
          setIsProcessingPopupVisible(false);
          authNavigate("/login", "iam", navigate);
          return;
        }

        if (data.status === "failed") {
          setIsProcessingPopupVisible(false);
          setPopupErrorMessage(data.message || "Generation failed");
          setIsErrorPopupVisible(true);
          return;
        }

        const generatedTags = data.data?.tags || [];

        const aiRows = generatedTags.map((item) => ({
          id: makeId(),
          picos: REVERSE_PICOS_ENUM_MAP[item.picos_category] || item.picos_category,
          subcategory: item.subcategory || "Unclassified",
          tag: item.tag,
          source: "AI-generated",
          frequency: item.sample_frequency || "",
        }));

        // The agent can silently drop `source` (the backend then defaults it to
        // "AI"), which would relabel manually added tags.
        const key = (picos, tag) =>
          `${picos}||${(tag || "").trim().toLowerCase().replace(/\s+/g, " ")}`;

        setRows((prev) => {
          if (mode === "replace") return aiRows;

          const byKey = new Map(prev.map((r) => [key(r.picos, r.tag), r]));

          if (mode === "add") {
            const fresh = aiRows.filter((g) => !byKey.has(key(g.picos, g.tag)));
            return [...prev, ...fresh];
          }

          // revise: reclassify existing tags in place AND append new ones.
          //
          // A tag missing from aiRows is NOT treated as a deletion. The backend's
          // corpus filter (_filter_and_compute_tag_frequencies) drops zero-match
          // tags before they reach us, so absence is ambiguous — and tags have no
          // db_id, only their text, so a wrong delete is unrecoverable.
          const aiByKey = new Map(aiRows.map((g) => [key(g.picos, g.tag), g]));

          const reclassified = prev.map((r) => {
            const g = aiByKey.get(key(r.picos, r.tag));
            if (!g) return r;
            return {
              ...r,                                  // id, source, tag stay
              subcategory: g.subcategory || r.subcategory,
              frequency: g.frequency || r.frequency,
            };
          });

          const fresh = aiRows.filter((g) => !byKey.has(key(g.picos, g.tag)));

          const movedCount = reclassified.filter(
            (r, i) => r.subcategory !== prev[i].subcategory
          ).length;
          console.info(
            `[Tags revise] ${movedCount} reclassified, ${fresh.length} added, ` +
            `${prev.length - aiRows.length > 0 ? prev.length - aiRows.length : 0} not returned (kept)`
          );

          return [...reclassified, ...fresh];
        });

        if (mode === "replace") {
          setExpandedCategories(new Set());
          setExpandedSubcategories(new Set());
        }
        setIsProcessingPopupVisible(false);
      } catch (error) {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(
          error?.response?.data?.message || "Failed to generate tags",
        );
        setIsErrorPopupVisible(true);
      }
    };

    const onGenerate = () => {
      if (!aiPrompt.trim()) return;
      if (!projectId) return;

      const hasExisting = rows.some((r) => (r.tag || "").trim().length > 0);
      if (hasExisting) {
        setShowGenerateConfirm(true);
      } else {
        doGenerate("replace");
      }
    };

    // Load from stageData
    useEffect(() => {
      if (!stageData?.tags?.length) return;

      setRows(
        stageData.tags.map((t) => ({
          id: makeId(),
          picos: REVERSE_PICOS_ENUM_MAP[t.picos_category] || t.picos_category,
          subcategory: t.subcategory || "Unclassified",
          tag: t.tag,
          source: REVERSE_TAG_SOURCE_ENUM_MAP[t.source] || t.source,
          frequency: t.sample_frequency,
        })),
      );
    }, [stageData]);

    // Get PICO color for a category
    const getCategoryColor = (catEnum) => {
      const key = normalizePicoKey(catEnum);
      return PICO_COLORS[key] || "#6c757d";
    };

    return (
      <div className="w-100 h-100 d-flex flex-column">
        {/* TOP BAR */}
        <div className="row align-items-end mb-2 ms-2">
          {/* LEFT ACTION */}
          <div className="col-5">
            <h6 className="ev-med-p-dark mb-0 d-flex justify-content-between align-items-center mb-0 pe-1 ev-bold-med">
              Tags
            </h6>
          </div>

          {/* AI SECTION */}
          <div className="col-7">
            <div className="d-flex align-items-center justify-content-between gap-2 pe-1">
              {/* Left title */}
              <span className="ev-reg-p-dark ev-bold-med ps-1 text-nowrap">
                Generate using AI <i className="bi bi-stars gradient-font6"></i>
              </span>

              {/* Right controls */}
              <div className="ms-1 d-flex align-items-center gap-2 flex-nowrap">
                {/* Random Abstract Sample */}
                <div className="d-flex align-items-center ev-card-2 py-1 px-2 ev-radius-xsmall  gap-2 overflow-hidden">
                  <span className="ev-xsmall-p-blue text-nowrap">
                    Random Abstract Sample
                  </span>

                  <input
                    type="number"
                    min="1"
                    className="form-control form-control-sm  ev-message-card6 ev-btn-xsmall ev-radius-xsmall text-center py-0"
                    value={sampleSize}
                    onChange={(e) => setSampleSize(e.target.value)}
                  />
                </div>

                {/* Use Protocol */}
                <div className="ev-card-2 py-1 px-2 ev-radius-xsmall d-flex align-items-center me-1">
                  <label
                    className="form-check form-switch p-0 m-0 d-flex align-items-center gap-2 ev-small-p-blue text-nowrap"
                    htmlFor="valueMessageToggle"
                  >
                    Use Protocol
                    <input
                      className="form-check-input custom-switch m-0"
                      type="checkbox"
                      id="valueMessageToggle"
                      checked={useProtocol}
                      onChange={(e) => setUseProtocol(e.target.checked)}
                    />
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row mb-2 ms-2">
          <div className="col-5">
            <p className="ev-reg-p-dark position-relative flex-grow-1 me-2 mb-0 d-block">
              Automatically organize papers for filtering
            </p>
          </div>
          <div className="col-7">
            <div className="position-relative flex-grow-1 me-2">
              <input
                type="text"
                className="formField4 w-100 slr-input-with-btn"
                placeholder="Add your prompt here"
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
              />
              <button
                type="button"
                className="ev-btn-gradient-fill-round ev-btn-reg slr-input-inner-btn ev-radius-xsmall"
                onClick={onGenerate}
              >
                Generate
              </button>
            </div>
          </div>
        </div>

        {/* TOTAL BADGE + EXPAND ALL */}
        <div className="col-12 d-flex align-items-center justify-content-between px-2 mb-0">
          <button
            type="button"
            className="ev-btn-light-fill ev-btn-small ev-bold-reg text-nowrap"
            onClick={toggleExpandAll}
          >
            <i className={`bi ${areAllExpanded ? "bi-arrows-collapse" : "bi-arrows-expand"} me-1`}></i>
            {areAllExpanded ? "Collapse All" : "Expand All"}
          </button>

          <span className="category-badge category-badge-5 text-nowrap">
            Total Tags: {totalTagsCount}
          </span>
        </div>

        {/* HEADERS FOR SOURCE AND FREQUENCY */}
        {sortedCategories.length > 0 && rows.length > 0 && (
            <div className="d-flex w-100 px-4 mb-2">
                <div className="flex-grow-1"></div>
                <div className="slr-tag-col-source">
                    <span className="ev-small-p-dark ev-bold-med ps-3">Source</span>
                </div>
                <div className="slr-tag-col-freq">
                    <span className="ev-small-p-dark ev-bold-med">Frequency</span>
                </div>
            </div>
        )}

        {/* HIERARCHICAL TREE WITH DND */}
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="w-100 scrollable-element vh-40 px-1 overflow-auto pb-0 pe-2 ">
            {sortedCategories.length === 0 && rows.length === 0 && (
              <div className="text-center py-4">
                <span className="ev-reg-p-dark">
                  No tags yet. Use "Generate" above or add tags manually.
                </span>
              </div>
            )}

            {sortedCategories.map((catEnum) => {
              const subs = grouped[catEnum] || {};
              const catLabel = REVERSE_PICOS_ENUM_MAP[catEnum] || catEnum;
              const catColor = getCategoryColor(catEnum);
              const isCatExpanded = expandedCategories.has(catEnum);
              const catTagCount = Object.values(subs).reduce(
                (sum, arr) => sum + arr.length,
                0,
              );

              return (
                <div key={catEnum} className="mb-2">
                  {/* Category Header */}
                  <div
                    className="slr-tag-category d-flex align-items-center justify-content-between ev-reg-p-dark"
                    onClick={() => toggleCategory(catEnum)}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <i
                        className={`bi ${isCatExpanded ? "bi-chevron-down" : "bi-chevron-right"} ev-fs-12`}
                      />
                     
                      <span className="ev-reg-p-dark ev-bold-med">
                        {catLabel}
                      </span>
                    </div>
                    <span className="ev-xsmall-p-dark">
                      {catTagCount} {catTagCount === 1 ? "tag" : "tags"}
                    </span>
                  </div>

                  {/* Subcategories */}
                  {isCatExpanded && (
                    <div className="ms-2 mt-1">
                      {Object.entries(subs).sort(([a], [b]) => {
                        if (a === "Unclassified") return 1;
                        if (b === "Unclassified") return -1;
                        return a.localeCompare(b);
                      }).map(([subName, subRows]) => {
                        const subKey = `${catEnum}::${subName}`;
                        const isSubExpanded = expandedSubcategories.has(subKey);

                        return (
                          <div key={subKey} className="mb-1">
                            {/* Subcategory Header */}
                            <div
                              className="slr-tag-subcategory d-flex align-items-center justify-content-between ev-reg-p-dark"
                              onClick={() => toggleSubcategory(subKey)}
                            >
                              <div className="d-flex align-items-center gap-2 ev-reg-p-dark">
                                <i
                                  className={`bi ${isSubExpanded ? "bi-chevron-down" : "bi-chevron-right"} ev-fs-12`}
                                />
                                <span className="ev-small-p-dark ev-bold-med">
                                  {subName}
                                </span>
                              </div>
                              <span className="ev-xxsmall-p-dark">
                                {subRows.length}
                              </span>
                            </div>

                            {/* Tag Items (Droppable) */}
                            {isSubExpanded && (
                              <Droppable droppableId={subKey}>
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    className={`slr-tag-items-container ${snapshot.isDraggingOver ? "slr-tag-drop-active" : ""}`}
                                  >
                                    {subRows.map((row, index) => {
                                      const isManual = (row.source || "")
                                        .toLowerCase()
                                        .includes("manually");
                                      const isAI = (row.source || "")
                                        .toLowerCase()
                                        .includes("ai");

                                      return (
                                        <Draggable
                                          key={row.id}
                                          draggableId={row.id}
                                          index={index}
                                        >
                                          {(dragProvided, dragSnapshot) => (
                                            <div
                                              ref={dragProvided.innerRef}
                                              {...dragProvided.draggableProps}
                                              className={`ms-4 ps-0 slr-tag-item w-100 py-0 mb-1 ${dragSnapshot.isDragging ? "slr-tag-dragging" : ""}`}
                                            >
                                              <div className="d-flex align-items-center w-100 gap-3">
                                                  {/* LEFT HALF: Drag + Input + Trash */}
                                                  <div className="d-flex align-items-center w-100 gap-1 p-0 py-1 ev-radius-xsmall ev-card-2 pe-1">
                                                      {/* Drag Handle */}
                                                      <div
                                                        {...dragProvided.dragHandleProps}
                                                        className="slr-tag-drag-handle avatar-tooltip"
                                                        data-tooltip="Drag to move"
                                                      >
                                                        <i className="bi bi-grip-vertical ev-fs-18 ms-2" />
                                                      </div>

                                                      {/* Tag Input */}
                                                      <div className="flex-grow-1 d-flex align-items-center ev-card-3 ev-radius-xsmall px-1 py-0 border border-light">
                                                        <input
                                                          type="text"
                                                          className="formField3 border-0 w-100 py-1 ev-radius-xsmall"
                                                          placeholder="Tag text"
                                                          value={row.tag}
                                                          onChange={(e) =>
                                                            updateRow(row.id, {
                                                              tag: e.target.value,
                                                            })
                                                          }
                                                        />
                                                        {/* Delete Inside Input container just to the right */}
                                                        <button
                                                          type="button"
                                                          className="ev-btn-red-fill-round ev-btn-small ev-radius-xsmall py-1 px-2 m-1 flex-shrink-0"
                                                          onClick={() =>
                                                            removeRow(row.id)
                                                          }
                                                        >
                                                          <i className="bi bi-trash3" />
                                                        </button>
                                                      </div>
                                                  </div>

                                                  {/* RIGHT HALF: Source + Frequency */}
                                                    <div className={`flex-shrink-0 d-flex align-items-center slr-tag-col-meta${isManual ? " ev-message-card3 ev-radius-xsmall" : ""}`}>
                                                      <div className="slr-tag-col-source d-flex align-items-center justify-content-start ps-3">
                                                          <span
                                                            className={`ev-reg-p-dark text-nowrap ev-bold-reg ${isAI ? "gradient-font6" : ""}`}
                                                          >
                                                            {row.source || "-"}
                                                          </span>
                                                      </div>

                                                      <div className="slr-tag-col-freq d-flex align-items-center justify-content-center">
                                                          <span className="ev-small-p-dark ev-bold-med text-nowrap">
                                                            {row.frequency || row.frequency === 0 ? row.frequency : "-"}
                                                          </span>
                                                      </div>
                                                  </div>
                                              </div>
                                            </div>
                                          )}
                                        </Draggable>
                                      );
                                    })}
                                    {provided.placeholder}

                                    {/* Add Tag Button */}
                                    <button
                                      type="button"
                                      className="ev-btn-lightblue-fill-round ev-btn-xsmall mt-1 ms-4"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        addTagInSubcategory(catEnum, subName);
                                      }}
                                    >
                                      <i className="bi bi-plus me-1" />
                                      Add Tag
                                    </button>
                                  </div>
                                )}
                              </Droppable>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </DragDropContext>

        {isProcessingPopupVisible && (
          <ProcessingPopup message={processingMessage} />
        )}

        {isErrorPopupVisible && (
          <ErrorPopup
            onClose={() => setIsErrorPopupVisible(false)}
            message={popupErrorMessage}
          />
        )}

        {showGenerateConfirm && (
          <div className="popup" onClick={() => setShowGenerateConfirm(false)}>
            <div
              className="popup-content slr-genmode-picker"
              onClick={(e) => e.stopPropagation()}
            >
              <p className="ev-med-p-blue ev-bold-med mb-1">You already have tags</p>
              <p className="ev-small-p-dark mb-3">
                Choose what AI should do with your{" "}
                {rows.filter((r) => (r.tag || "").trim()).length} existing tags.
              </p>

              {[
                { key: "add", title: "Keep and add",
                  desc: "Your tags stay as they are. AI appends new ones based on your prompt." },
                { key: "revise", title: "Keep and revise",
                  desc: "AI reclassifies your tags and adds new ones in one go. Tag text is never changed and nothing is removed — delete the tags you don't want yourself." },
                { key: "replace", title: "Replace everything",
                  desc: "All current tags are discarded and regenerated, including any you added manually." },
              ].map((m) => (
                <button
                  key={m.key}
                  className="slr-genmode-option"
                  onClick={() => { setShowGenerateConfirm(false); doGenerate(m.key); }}
                >
                  <span className="ev-reg-p-dark ev-bold-reg d-block">{m.title}</span>
                  <span className="ev-small-p-dark">{m.desc}</span>
                </button>
              ))}

              <div className="d-flex justify-content-end mt-2">
                <button
                  className="ev-btn-light-fill ev-btn-small"
                  onClick={() => setShowGenerateConfirm(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  },
);

export default ScreeningTaggingForm;
